import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, Crown, UserPlus, X, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_META = {
  owner:  { label: "Owner",  color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  editor: { label: "Editor", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  viewer: { label: "Viewer", color: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
};

/**
 * ProjectMembersDialog — invite users as editor/viewer on a project.
 * Props: open, onOpenChange, projectId, projectName
 */
export default function ProjectMembersDialog({ open, onOpenChange, projectId, projectName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviting, setInviting] = useState(false);

  const auth = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/members`, { headers: auth() });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, projectId]);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/members`,
        { email: inviteEmail.trim().toLowerCase(), role: inviteRole },
        { headers: auth() },
      );
      toast.success("Member added");
      setInviteEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add member");
    } finally {
      setInviting(false);
    }
  };

  const remove = async (memberId, email) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${email} from this project?`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/members/${memberId}`, { headers: auth() });
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await axios.patch(
        `${API}/projects/${projectId}/members/${memberId}`,
        { role: newRole },
        { headers: auth() },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update role");
    }
  };

  const isOwner = data?.current_user_role === "owner";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" />
            Members · {projectName || "Project"}
          </DialogTitle>
          <DialogDescription>
            Invite collaborators by email. They&apos;ll be able to open the project from their &ldquo;Shared with me&rdquo; list.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Owner card */}
            {data?.owner && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-950/40">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Crown className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-200">{data.owner.name || data.owner.email}</div>
                    <div className="text-xs text-slate-500">{data.owner.email}</div>
                  </div>
                </div>
                <Badge variant="outline" className={ROLE_META.owner.color}>Owner</Badge>
              </div>
            )}

            {/* Members list */}
            {(data?.members || []).map((m) => (
              <div key={m.user_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-950/40" data-testid={`member-row-${m.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-sm text-slate-300">
                    {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-200">{m.name || m.email}</div>
                    <div className="text-xs text-slate-500">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isOwner ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                      <SelectTrigger className="w-[110px] bg-slate-800 border-slate-700 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={ROLE_META[m.role]?.color || ROLE_META.viewer.color}>
                      {ROLE_META[m.role]?.label || m.role}
                    </Badge>
                  )}
                  {isOwner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(m.user_id, m.email)}
                      className="text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                      data-testid={`remove-member-${m.user_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {isOwner && (
              <div className="border-t border-slate-800 pt-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                  <UserPlus className="w-3 h-3" /> Invite by email
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="teammate@church.org"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="bg-slate-950 border-slate-800 flex-1"
                    data-testid="invite-email-input"
                  />
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="w-[110px] bg-slate-950 border-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={invite}
                    disabled={inviting || !inviteEmail.trim()}
                    className="bg-amber-500 text-black hover:bg-amber-400"
                    data-testid="invite-submit-btn"
                  >
                    {inviting ? "..." : "Add"}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  The person must already have a SlideCon account.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
